import * as React from "react";
import { HelmetProvider } from "react-helmet-async";
import { QueryClientProvider } from "@tanstack/react-query";
import { handleSuccessMessage } from "@/shared/lib";
import ThemeProvider from "./ThemeProvider";
import { LanguageProvider } from "./LanguageProvider";
import { CurrencyProvider } from "./CurrencyProvider";
import { GlobalErrorBoundary, SuspenseLoading } from "@/shared/ui";
import { PolicyAcceptanceProvider } from "./PolicyAcceptanceProvider";
import { RealtimeToastHost } from "./RealtimeToastHost";
import { createQueryClient } from "@/shared/config/queryClient";
import { useGlobalError } from "@/shared/hooks";
// Register the policy enforcement interceptor at app bootstrap.
// Must be imported here (app layer) — not in shared — because it
// imports from features/ which is above shared in the FSD hierarchy.
import '@/app/interceptors/policyEnforcement';

// Lazy: ErrorStatePage pulls in motion/framer-motion (~124 kB). A static
// import here would put that in the entry bundle of every page load just to
// cover the rare global-error screen.
const ErrorStatePage = React.lazy(
  () => import("@/pages/ErrorState/ErrorStatePage"),
);

export const queryClient = createQueryClient();

/** Reads error state from the store and renders ErrorStatePage with the right props. */
function ErrorFallback() {
  const {
    statusCode,
    title,
    description,
    errorPayload,
    clearError,
    lastFailedRequest,
  } = useGlobalError();

  const handleRetry = () => {
    clearError();
    if (lastFailedRequest) {
      lastFailedRequest();
    } else {
      window.location.reload();
    }
  };

  const handleGoHome = () => {
    clearError();
    window.location.href = "/";
  };

  return (
    <React.Suspense fallback={<SuspenseLoading />}>
      <ErrorStatePage
        statusCode={statusCode}
        title={title || undefined}
        description={description || undefined}
        errorPayload={errorPayload}
        onRetry={handleRetry}
        onGoHome={handleGoHome}
      />
    </React.Suspense>
  );
}

export function AppProviders({ children }: { children: React.ReactNode }) {
  React.useEffect(() => {
    const handleOnline = () => {
      handleSuccessMessage(null, { key: "success.app.backOnline" });
    };

    const handleOffline = () => {
      const { setError } = useGlobalError.getState();
      setError(
        0,
        "You're offline.",
        "We can't reach the server. Check your internet connection and try again.",
        {
          type: "NETWORK_ERROR",
          message: "Browser went offline",
          code: "OFFLINE",
          path: window.location.pathname,
          timestamp: new Date().toISOString(),
        },
      );
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return (
    <React.Suspense fallback={<SuspenseLoading />}>
      <ThemeProvider>
        <RealtimeToastHost />
        <LanguageProvider>
          {/* Outside the error boundary and PolicyAcceptanceProvider, not
              inside them. Every error screen this app can show is an
              ErrorStatePage, and ErrorStatePage renders <Helmet> — so with
              HelmetProvider nested below, the boundary's own fallback threw
              "Cannot read properties of undefined (reading 'add')" the moment
              it tried to render. An error UI that crashes when an error
              happens is the one that has to work. */}
          <HelmetProvider>
            <QueryClientProvider client={queryClient}>
              <CurrencyProvider>
                <GlobalErrorBoundary fallback={<ErrorFallback />}>
                  <PolicyAcceptanceProvider>{children}</PolicyAcceptanceProvider>
                </GlobalErrorBoundary>
              </CurrencyProvider>
            </QueryClientProvider>
          </HelmetProvider>
        </LanguageProvider>
      </ThemeProvider>
    </React.Suspense>
  );
}
